import React, { useEffect } from 'react'
import { BtnV2, Icon, Lozenge, l } from '@manychat/manyui'

import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { Behavior } from 'apps/aiAutomation/components/Behavior'
import {
  DisableScenarioConfirm,
  useAutomationStatusToggle,
} from 'apps/aiAutomation/components/DisableScenarioConfirm'
import { HintCard } from 'apps/aiAutomation/components/HintCard'
import { EmptyState } from 'apps/aiAutomation/components/PositiveCommentReplies/components/EmptyState'
import { SidebarHeader } from 'apps/aiAutomation/components/SidebarHeader'
import { AiAutomationAnalytics } from 'apps/aiAutomation/lib/analytics'
import { SidebarViewType } from 'apps/aiAutomation/lib/sidebar'
import { AiAutomationBehaviorSelectors } from 'apps/aiAutomation/store/behavior'
import { AiAlwaysOnAutomationsSelectors } from 'apps/aiAutomation/store/core'
import { LoaderStatus } from 'apps/aiAutomation/store/core/lib/types'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import {
  AiAutomationToneOfVoiceActions,
  AiAutomationToneOfVoiceSelectors,
} from 'apps/aiAutomation/store/toneOfVoice'
import { AiAutomationScenario } from 'shared/api/requests/aiAutomation/schemas'
import { AiAutomationTovState } from 'shared/api/requests/aiToneOfVoice/schemas'
import { AccountFlag } from 'utils/services/featureFlagsService/constants'
import { useAccountReleaseToggle } from 'utils/services/featureFlagsService/releaseTogglesHooks'

import BubbleIcon from './assets/bubble.svg'
import { Showcase } from './components/Showcase'

import cm from './styles.module.css'

export const PositiveCommentReplies = () => {
  const dispatch = useAppDispatch()

  const isBehaviorPageEnabled = useAccountReleaseToggle(AccountFlag.ENABLE_AI_BEHAVIOR_PAGE)

  const isBehaviorExists = useAppSelector(AiAutomationBehaviorSelectors.getIsBehaviorExists)
  const isSocialEngagementPublished = useAppSelector(
    AiAlwaysOnAutomationsSelectors.getIsSocialEngagementPublished,
  )
  const isStatusUpdating =
    useAppSelector(AiAlwaysOnAutomationsSelectors.getLoaderStatus) ===
    LoaderStatus.SOCIAL_ENGAGEMENT_STATUS_UPDATING
  const tovState = useAppSelector(AiAutomationToneOfVoiceSelectors.getTovState)
  const isOldVersionUsed = useAppSelector(AiAutomationToneOfVoiceSelectors.getIsOldVersion)

  const { toggleStatus, isConfirmModalOpen, closeConfirmModal } = useAutomationStatusToggle({
    scenario: AiAutomationScenario.SOCIAL_ENGAGEMENT,
    source: 'automation_editor',
  })

  const reinitSetup = () => {
    if (tovState === AiAutomationTovState.FINISHED) {
      AiAutomationAnalytics.logTovRateProcessStarted({
        is_first_generation: false,
      })

      dispatch(AiAutomationToneOfVoiceActions.initializeFx())
    }
    dispatch(AiAutomationLayoutActions.viewOpened({ view: SidebarViewType.TOV_SETUP }))
  }

  const isTovGenerated = tovState === AiAutomationTovState.FINISHED
  const isGoLiveButtonDisabled = !isTovGenerated || !isBehaviorExists

  useEffect(() => {
    if (
      tovState !== AiAutomationTovState.FINISHED &&
      tovState !== AiAutomationTovState.NOT_STARTED &&
      !isBehaviorPageEnabled
    ) {
      dispatch(AiAutomationLayoutActions.viewOpened({ view: SidebarViewType.TOV_SETUP }))
    }
  }, [dispatch, isBehaviorPageEnabled, tovState])

  if (isBehaviorPageEnabled && !isBehaviorExists) {
    return <Behavior title={l.translate('AI Comments')} />
  }

  return (
    <section>
      <SidebarHeader
        title={l.translate('AI Comments')}
        button={
          <div className="d-flex gap-sm">
            {isBehaviorPageEnabled && (
              <div className="d-flex gap-xxs text-secondary align-center">
                <Icon.Ai size={20} />
                {l.translate('Test AI coming soon')}
                <Icon.Question size={16} data-title="Testing AI Comments isn't available yet." />
              </div>
            )}

            <BtnV2
              onClick={toggleStatus}
              data-test-id="ai-automation/positive-comment-replies-status-change-button"
              variant={isSocialEngagementPublished ? 'danger-light' : 'primary'}
              loading={isStatusUpdating}
              disabled={isSocialEngagementPublished ? false : isGoLiveButtonDisabled}
            >
              {isSocialEngagementPublished ? l.translate('Stop') : l.translate('Go Live')}
            </BtnV2>
          </div>
        }
      />

      <div className={cm.content}>
        <HintCard
          title={l.translate('AI will reply in your tone when')}
          icon="IncreaseEngagement"
          text={l.translate('Someone leaves a nice comment')}
        />

        {isTovGenerated && !isOldVersionUsed && !isBehaviorPageEnabled && (
          <div className="m-t-xl">
            <h4 className="m-b text-subheading">{l.translate('Tone matched!')}</h4>

            <div className={cm.completedSetup}>
              <Icon.DoubleTick className="m-r-xs" />

              <p className="m-0">{l.translate('AI will now reply just like you')}</p>

              <BtnV2 onClick={reinitSetup} variant="ghost-light">
                {l.translate('Adjust tone')}
              </BtnV2>
            </div>
          </div>
        )}

        {isTovGenerated && isOldVersionUsed && !isBehaviorPageEnabled && (
          <div className="m-t-xl">
            <h4 className="m-b text-subheading d-flex align-center gap-xs">
              <span>{l.translate('AI Comments just got smarter')}</span>
              <Lozenge type="new" />
            </h4>
            <div className={cm.newTovCard}>
              <img src={BubbleIcon} alt="" />
              <div className="d-flex flex-col">
                <p className="font-medium">{l.translate('You’ve helped AI match your tone')}</p>
                <p className="m-t-xxs">
                  {l.translate('Now you can personalize its tone and voice to sound more like you')}
                </p>
                <BtnV2 className="m-t-xs align-self-end" onClick={reinitSetup} variant="primary">
                  {l.translate('Set AI’s tone')}
                </BtnV2>
              </div>
            </div>
          </div>
        )}

        {!isTovGenerated && !isBehaviorPageEnabled && <EmptyState />}

        {isBehaviorPageEnabled && (
          <p className="m-t-xs text-body-sm text-secondary">
            {l.translate("AI replies to nice comments based on the behavior you've set.")}
          </p>
        )}

        {isBehaviorPageEnabled && <Showcase />}
      </div>

      {isConfirmModalOpen && (
        <DisableScenarioConfirm
          onClose={closeConfirmModal}
          scenario={AiAutomationScenario.SOCIAL_ENGAGEMENT}
          source="automation_editor"
        />
      )}
    </section>
  )
}
